import { useCallback, useEffect, useState } from "react";
import { setDocumentTitleMask } from "../documentTitle/documentTitle.js";

export const DEFAULT_CURTAIN_CONCEALED_THRESHOLD_MS = 300_000;

/** Threshold for the logins in {@link EXTENDED_CURTAIN_THRESHOLD_EMAILS}. */
const EXTENDED_CURTAIN_CONCEALED_THRESHOLD_MS = 4 * 60 * 60 * 1_000;

/** Logins that get the extended threshold, in both Admin and HQ. */
const EXTENDED_CURTAIN_THRESHOLD_EMAILS: ReadonlySet<string> = new Set([
  "dennis@alpharun.com",
  "henry@alpharun.com",
  "demo-yosemite@alpharun.com",
  "jeremy@alpharun.com",
  "kevin@alpharun.com",
  "steven@alpharun.com",
  "demo-acadia@alpharun.com",
]);

/** The concealed-time threshold for the signed-in login. */
export function getCurtainConcealedThresholdMs(
  email: string | null | undefined,
): number {
  return EXTENDED_CURTAIN_THRESHOLD_EMAILS.has(email?.toLowerCase() ?? "")
    ? EXTENDED_CURTAIN_CONCEALED_THRESHOLD_MS
    : DEFAULT_CURTAIN_CONCEALED_THRESHOLD_MS;
}

export interface UsePrivacyCurtainOptions {
  /** Arms the curtain. While false the tab is never covered. */
  isEnabled: boolean;
  /**
   * Browser-tab title shown while the curtain covers the app, so the tab strip
   * doesn't leak what the covered page is about. Page titles set through
   * `setDocumentPageTitle` while covered stay hidden until the curtain is
   * dismissed, which shows the latest one.
   */
  maskedDocumentTitle: string;
  /** How long the tab must stay concealed before the curtain drops. */
  concealedThresholdMs?: number;
}

export interface PrivacyCurtainState {
  /** True while the app should be covered by an opaque curtain. */
  isCurtainActive: boolean;
  /** Dismisses the curtain (the user clicked to show the app). */
  revealApp: () => void;
}

/**
 * Screen-share privacy curtain trigger: once the app has been concealed for
 * longer than the threshold, the curtain activates and stays up until the
 * user explicitly reveals the app.
 *
 * "Concealed" means the tab is hidden (`visibilitychange`) **or** the window
 * has lost focus (`blur`) — switching to another application can leave the
 * tab technically visible, so visibility alone would miss the common
 * screen-share slip of clicking back into a still-visible browser window.
 * The blur source also fires when focus moves into an iframe or devtools;
 * dismissing the curtain after long stretches there is an accepted cost.
 */
export function usePrivacyCurtain(
  options: UsePrivacyCurtainOptions,
): PrivacyCurtainState {
  const {
    isEnabled,
    maskedDocumentTitle,
    concealedThresholdMs = DEFAULT_CURTAIN_CONCEALED_THRESHOLD_MS,
  } = options;

  const [isCurtainActive, setIsCurtainActive] = useState(false);

  useEffect(() => {
    if (!isEnabled) {
      setIsCurtainActive(false);
      return;
    }

    let isTabHidden = document.visibilityState === "hidden";
    let isWindowBlurred = !document.hasFocus();
    let concealedAtMs: number | null =
      isTabHidden || isWindowBlurred ? Date.now() : null;
    let concealedTimeoutId: number | undefined;

    // Drop the curtain while the app is still concealed so no covered content
    // can paint on the first exposed frame. Browsers throttle background
    // timers, so the elapsed-time check on re-exposure below is the backstop.
    const armConcealedTimeout = () => {
      concealedTimeoutId = window.setTimeout(() => {
        setIsCurtainActive(true);
      }, concealedThresholdMs);
    };

    if (concealedAtMs !== null) {
      armConcealedTimeout();
    }

    const handleConcealmentChange = () => {
      const isConcealed = isTabHidden || isWindowBlurred;
      if (isConcealed && concealedAtMs === null) {
        concealedAtMs = Date.now();
        armConcealedTimeout();
      } else if (!isConcealed && concealedAtMs !== null) {
        window.clearTimeout(concealedTimeoutId);
        if (Date.now() - concealedAtMs >= concealedThresholdMs) {
          setIsCurtainActive(true);
        }
        concealedAtMs = null;
      }
    };

    const handleVisibilityChange = () => {
      isTabHidden = document.visibilityState === "hidden";
      handleConcealmentChange();
    };
    const handleWindowBlur = () => {
      isWindowBlurred = true;
      handleConcealmentChange();
    };
    const handleWindowFocus = () => {
      isWindowBlurred = false;
      handleConcealmentChange();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      window.clearTimeout(concealedTimeoutId);
    };
  }, [isEnabled, concealedThresholdMs]);

  useEffect(() => {
    if (!isCurtainActive) {
      return;
    }
    setDocumentTitleMask(maskedDocumentTitle);
    return () => {
      setDocumentTitleMask(null);
    };
  }, [isCurtainActive, maskedDocumentTitle]);

  const revealApp = useCallback(() => {
    setIsCurtainActive(false);
  }, []);

  return { isCurtainActive, revealApp };
}
